/**
 * Multi-calendar conversion library.
 *
 * Provides adapters for 10 calendar systems used across the site's audience.
 * The DB stores all dates in Gregorian; this layer is purely for display.
 *
 * Adapters use:
 *  - `Intl.DateTimeFormat` for localized month names (built into Node 22 / browsers)
 *  - `Temporal` polyfill for bidirectional conversion (9 of 10 systems)
 *  - `nepali-date-converter` for Bikram Sambat (Nepal) — not in ECMA-402
 *
 * SSR-safe: every adapter must produce identical output server vs. client.
 *
 * See PRD: .claude/docs/prd-multi-calendar-toggle.md
 */

import { Temporal } from '@js-temporal/polyfill';
import NepaliDate from 'nepali-date-converter';

export type CalendarSystem =
  | 'gregorian'
  | 'islamic-umalqura'
  | 'hebrew'
  | 'persian'
  | 'buddhist'
  | 'ethiopic'
  | 'japanese'
  | 'roc'
  | 'indian'
  | 'bikram';

export const CALENDAR_SYSTEMS: readonly CalendarSystem[] = [
  'gregorian',
  'islamic-umalqura',
  'hebrew',
  'persian',
  'buddhist',
  'ethiopic',
  'japanese',
  'roc',
  'indian',
  'bikram',
] as const;

export interface CalendarParts {
  year: number;
  month: number;       // 1-based
  day: number;         // 1-based
  era?: string;        // e.g. 'reiwa' for japanese, 'ethiopic' for ethiopic
  eraYear?: number;    // displayable year-in-era (e.g. 8 for Reiwa 8)
}

export interface CalendarSystemAdapter {
  id: CalendarSystem;
  /** ICU calendar tag for use in `Intl` (`en-u-ca-<id>`) — same as `id` for Intl-native, omitted for bikram. */
  intlCalendar?: string;
  /** First day of week (0=Sunday, 1=Monday, 6=Saturday). */
  weekStart: 0 | 1 | 6;
  /** Display name for this calendar system, in the given language. */
  displayName(lang: string): string;
  /** Localized month name. `month` is 1-based. */
  monthName(year: number, month: number, lang: string): string;
  /** How many months in this year (12 for most; 13 for leap-year hebrew & ethiopic). */
  monthsInYear(year: number): number;
  /** How many days in this calendar-month (variable for lunar/lunisolar systems). */
  daysInMonth(year: number, month: number): number;
  /** Convert a Gregorian Date → calendar-system parts. */
  fromGregorian(d: Date): CalendarParts;
  /** Construct a Gregorian Date from calendar-system parts. */
  toGregorian(year: number, month: number, day: number): Date;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function gregorianFromDate(d: Date): Temporal.PlainDate {
  return Temporal.PlainDate.from({
    year: d.getUTCFullYear(),
    month: d.getUTCMonth() + 1,
    day: d.getUTCDate(),
  });
}

function plainDateToGregorianDate(pd: Temporal.PlainDate): Date {
  const iso = pd.withCalendar('iso8601');
  return new Date(Date.UTC(iso.year, iso.month - 1, iso.day));
}

/** Adapter factory for any calendar supported natively by `Temporal` + `Intl`. */
function makeIntlAdapter(opts: {
  id: CalendarSystem;
  intlCalendar: string;
  weekStart: 0 | 1 | 6;
  displayNames: Record<string, string>;
  /** When true, prefer `eraYear` over `year` for display (japanese, ethiopic). */
  useEraYear?: boolean;
}): CalendarSystemAdapter {
  const fallbackName = opts.displayNames.en ?? opts.id;

  function pdFromParts(year: number, month: number, day: number): Temporal.PlainDate {
    return Temporal.PlainDate.from({ year, month, day, calendar: opts.intlCalendar });
  }

  return {
    id: opts.id,
    intlCalendar: opts.intlCalendar,
    weekStart: opts.weekStart,
    displayName(lang: string): string {
      return opts.displayNames[lang] ?? opts.displayNames[lang.split('-')[0]] ?? fallbackName;
    },
    monthName(year: number, month: number, lang: string): string {
      const pd = pdFromParts(year, month, 1);
      const greg = plainDateToGregorianDate(pd);
      return new Intl.DateTimeFormat(`${lang}-u-ca-${opts.intlCalendar}`, { month: 'long' })
        .format(greg);
    },
    monthsInYear(year: number): number {
      return pdFromParts(year, 1, 1).monthsInYear;
    },
    daysInMonth(year: number, month: number): number {
      return pdFromParts(year, month, 1).daysInMonth;
    },
    fromGregorian(d: Date): CalendarParts {
      const pd = gregorianFromDate(d).withCalendar(opts.intlCalendar);
      // For era-based calendars (japanese, roc), expose `year` as the
      // continuous-era year that round-trips through Temporal, plus eraYear for display.
      return {
        year: pd.year,
        month: pd.month,
        day: pd.day,
        era: pd.era,
        eraYear: pd.eraYear,
      };
    },
    toGregorian(year: number, month: number, day: number): Date {
      return plainDateToGregorianDate(pdFromParts(year, month, day));
    },
  };
}

// ---------------------------------------------------------------------------
// Adapters
// ---------------------------------------------------------------------------

const gregorianAdapter: CalendarSystemAdapter = {
  id: 'gregorian',
  intlCalendar: 'gregory',
  weekStart: 0,
  displayName(lang) {
    const map: Record<string, string> = {
      en: 'Gregorian', ar: 'ميلادي', he: 'גרגוריאני', fa: 'میلادی',
      th: 'ปฏิทินสุริยคติ', id: 'Gregorian', ja: 'グレゴリオ暦', zh: '公历',
    };
    return map[lang] ?? map[lang.split('-')[0]] ?? 'Gregorian';
  },
  monthName(_y, month, lang) {
    return new Intl.DateTimeFormat(lang, { month: 'long' })
      .format(new Date(Date.UTC(2000, month - 1, 1)));
  },
  monthsInYear() { return 12; },
  daysInMonth(year, month) {
    return new Date(Date.UTC(year, month, 0)).getUTCDate();
  },
  fromGregorian(d) {
    return { year: d.getUTCFullYear(), month: d.getUTCMonth() + 1, day: d.getUTCDate() };
  },
  toGregorian(year, month, day) {
    return new Date(Date.UTC(year, month - 1, day));
  },
};

const islamicAdapter = makeIntlAdapter({
  id: 'islamic-umalqura',
  intlCalendar: 'islamic-umalqura',
  weekStart: 6,
  displayNames: {
    en: 'Hijri', ar: 'هجري', fa: 'هجری قمری', ur: 'ہجری',
    id: 'Hijriyah', ms: 'Hijrah', tr: 'Hicri',
  },
});

const hebrewAdapter = makeIntlAdapter({
  id: 'hebrew',
  intlCalendar: 'hebrew',
  weekStart: 0,
  displayNames: { en: 'Hebrew', he: 'עברי', ar: 'عبري', fr: 'Hébreu', es: 'Hebreo' },
});

const persianAdapter = makeIntlAdapter({
  id: 'persian',
  intlCalendar: 'persian',
  weekStart: 6,
  displayNames: { en: 'Persian (Solar Hijri)', fa: 'هجری شمسی', ar: 'فارسي', ps: 'لمریز' },
});

const buddhistAdapter = makeIntlAdapter({
  id: 'buddhist',
  intlCalendar: 'buddhist',
  weekStart: 0,
  displayNames: {
    en: 'Buddhist Era', th: 'พุทธศักราช', lo: 'ພຸດທະສັກກະຫຼາດ',
    km: 'ពុទ្ធសករាជ', si: 'බුද්ධ වර්ෂ',
  },
});

/**
 * Ethiopic calendar via pure arithmetic instead of @js-temporal/polyfill.
 *
 * The polyfill's ethiopic calendar depends on the runtime ICU's era codes, and
 * on some Linux ICU builds (incl. GitLab CI's node:22) every era-based op —
 * reading `.year`/`.eraYear` or constructing from `{ era, eraYear }` — throws
 * "Era am (ISO year N) was not matched by any era". The Ethiopic calendar is
 * purely arithmetic (12×30-day months + a 5/6-day Pagumē; leap when year%4===3),
 * so we convert via Julian Day Number with no ICU dependency. Verified to match
 * the polyfill exactly across 2023–2029 (0 mismatches). Month *names* still come
 * from native `Intl`, which is reliable (unlike the polyfill's Temporal path).
 * (almanak-75lg)
 */
const ETHIOPIC_EPOCH_JDN = 1724221; // JDN of 1 Meskerem 1 EE (Amete Mihret)
const ethFloor = Math.floor;

function gregorianToJDN(y: number, m: number, d: number): number {
  const a = ethFloor((14 - m) / 12);
  const yy = y + 4800 - a;
  const mm = m + 12 * a - 3;
  return d + ethFloor((153 * mm + 2) / 5) + 365 * yy + ethFloor(yy / 4)
    - ethFloor(yy / 100) + ethFloor(yy / 400) - 32045;
}
function jdnToGregorian(jdn: number): { y: number; m: number; d: number } {
  const a = jdn + 32044;
  const b = ethFloor((4 * a + 3) / 146097);
  const c = a - ethFloor((146097 * b) / 4);
  const dd = ethFloor((4 * c + 3) / 1461);
  const e = c - ethFloor((1461 * dd) / 4);
  const m = ethFloor((5 * e + 2) / 153);
  return {
    d: e - ethFloor((153 * m + 2) / 5) + 1,
    m: m + 3 - 12 * ethFloor(m / 10),
    y: 100 * b + dd - 4800 + ethFloor(m / 10),
  };
}
function ethiopicToJDN(y: number, m: number, d: number): number {
  return ETHIOPIC_EPOCH_JDN + 365 * (y - 1) + ethFloor(y / 4) + 30 * (m - 1) + (d - 1);
}
function jdnToEthiopic(jdn: number): { y: number; m: number; d: number } {
  const n = jdn - ETHIOPIC_EPOCH_JDN;
  const y = ethFloor((4 * n + 1463) / 1461);
  const dayOfYear = n - (365 * (y - 1) + ethFloor(y / 4));
  return { y, m: ethFloor(dayOfYear / 30) + 1, d: (dayOfYear % 30) + 1 };
}

const ETHIOPIC_DISPLAY_NAMES: Record<string, string> = {
  en: 'Ethiopian', am: 'የኢትዮጵያ ዘመን አቆጣጠር', ti: 'ኢትዮጵያዊ ዓውደ-ኣዋርሕ',
};

const ethiopicAdapter: CalendarSystemAdapter = {
  id: 'ethiopic',
  intlCalendar: 'ethiopic',
  weekStart: 0,
  displayName(lang: string): string {
    return ETHIOPIC_DISPLAY_NAMES[lang] ?? ETHIOPIC_DISPLAY_NAMES[lang.split('-')[0]] ?? 'Ethiopian';
  },
  monthName(year: number, month: number, lang: string): string {
    const g = jdnToGregorian(ethiopicToJDN(year, month, 1));
    const greg = new Date(Date.UTC(g.y, g.m - 1, g.d));
    return new Intl.DateTimeFormat(`${lang}-u-ca-ethiopic`, { month: 'long', timeZone: 'UTC' }).format(greg);
  },
  monthsInYear(): number {
    return 13; // 12 months + Pagumē
  },
  daysInMonth(year: number, month: number): number {
    if (month <= 12) return 30;
    return year % 4 === 3 ? 6 : 5; // Pagumē: 6 days in a leap year
  },
  fromGregorian(d: Date): CalendarParts {
    const { y, m, d: day } = jdnToEthiopic(gregorianToJDN(d.getUTCFullYear(), d.getUTCMonth() + 1, d.getUTCDate()));
    return { year: y, month: m, day, era: 'ethiopic', eraYear: y };
  },
  toGregorian(year: number, month: number, day: number): Date {
    const g = jdnToGregorian(ethiopicToJDN(year, month, day));
    return new Date(Date.UTC(g.y, g.m - 1, g.d));
  },
};

const japaneseAdapter = makeIntlAdapter({
  id: 'japanese',
  intlCalendar: 'japanese',
  weekStart: 0,
  displayNames: { en: 'Japanese (Reiwa era)', ja: '和暦', zh: '日本年号' },
  useEraYear: true,
});

const rocAdapter = makeIntlAdapter({
  id: 'roc',
  intlCalendar: 'roc',
  weekStart: 0,
  displayNames: { en: 'Minguo (ROC)', zh: '民國紀年', 'zh-Hant': '民國紀年' },
  useEraYear: true,
});

const indianAdapter = makeIntlAdapter({
  id: 'indian',
  intlCalendar: 'indian',
  weekStart: 0,
  displayNames: { en: 'Indian National (Saka)', hi: 'भारतीय राष्ट्रीय शक', bn: 'ভারতীয় শক', ta: 'இந்திய சகா' },
});

// Bikram Sambat — not in Intl/Temporal, custom adapter
const BIKRAM_MONTHS_EN = [
  'Baisakh', 'Jestha', 'Asar', 'Shrawan', 'Bhadra', 'Ashwin',
  'Kartik', 'Mangsir', 'Poush', 'Magh', 'Falgun', 'Chaitra',
];
const BIKRAM_MONTHS_NE = [
  'बैशाख', 'जेठ', 'असार', 'श्रावण', 'भदौ', 'आश्विन',
  'कार्तिक', 'मंसिर', 'पौष', 'माघ', 'फाल्गुण', 'चैत्र',
];

const bikramAdapter: CalendarSystemAdapter = {
  id: 'bikram',
  weekStart: 0,
  displayName(lang) {
    return lang.startsWith('ne') ? 'विक्रम संवत्' : 'Bikram Sambat';
  },
  monthName(_y, month, lang) {
    const idx = ((month - 1) % 12 + 12) % 12;
    return lang.startsWith('ne') ? BIKRAM_MONTHS_NE[idx] : BIKRAM_MONTHS_EN[idx];
  },
  monthsInYear() { return 12; },
  daysInMonth(year, month) {
    // nepali-date-converter does not expose direct days-in-month;
    // compute by stepping to next month and diffing.
    const first = new NepaliDate(year, month - 1, 1).toJsDate();
    const next = new NepaliDate(month === 12 ? year + 1 : year, month === 12 ? 0 : month, 1).toJsDate();
    return Math.round((next.getTime() - first.getTime()) / 86_400_000);
  },
  fromGregorian(d) {
    const nep = new NepaliDate(new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate())));
    return { year: nep.getYear(), month: nep.getMonth() + 1, day: nep.getDate() };
  },
  toGregorian(year, month, day) {
    const nep = new NepaliDate(year, month - 1, day);
    const d = nep.toJsDate();
    return new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  },
};

// ---------------------------------------------------------------------------
// Registry + lookup
// ---------------------------------------------------------------------------

const ADAPTERS: Record<CalendarSystem, CalendarSystemAdapter> = {
  gregorian: gregorianAdapter,
  'islamic-umalqura': islamicAdapter,
  hebrew: hebrewAdapter,
  persian: persianAdapter,
  buddhist: buddhistAdapter,
  ethiopic: ethiopicAdapter,
  japanese: japaneseAdapter,
  roc: rocAdapter,
  indian: indianAdapter,
  bikram: bikramAdapter,
};

export function getCalendarAdapter(id: CalendarSystem): CalendarSystemAdapter {
  return ADAPTERS[id] ?? gregorianAdapter;
}

export function isCalendarSystem(s: string): s is CalendarSystem {
  return (CALENDAR_SYSTEMS as readonly string[]).includes(s);
}

// ---------------------------------------------------------------------------
// Locale → default calendar mapping (Bead 3)
// ---------------------------------------------------------------------------

const LANG_DEFAULTS: Record<string, CalendarSystem> = {
  ar: 'islamic-umalqura',
  ur: 'islamic-umalqura',
  he: 'hebrew',
  fa: 'persian',
  ps: 'persian',
  th: 'buddhist',
  lo: 'buddhist',
  km: 'buddhist',
  si: 'buddhist',
  ne: 'bikram',
  am: 'ethiopic',
  ti: 'ethiopic',
  ja: 'japanese',
};

const COUNTRY_OVERRIDES: Record<string, CalendarSystem> = {
  // Country code → preferred calendar (overrides lang-based default)
  IL: 'hebrew',
  IR: 'persian',
  AF: 'persian',
  ET: 'ethiopic',
  ER: 'ethiopic',
  NP: 'bikram',
  TH: 'buddhist',
  LA: 'buddhist',
  KH: 'buddhist',
  MM: 'buddhist',
  LK: 'buddhist',
  JP: 'japanese',
  TW: 'roc',
  // Muslim-majority — Hijri default even if lang is en
  SA: 'islamic-umalqura',
  AE: 'islamic-umalqura',
  KW: 'islamic-umalqura',
  BH: 'islamic-umalqura',
  OM: 'islamic-umalqura',
  QA: 'islamic-umalqura',
  JO: 'islamic-umalqura',
  LB: 'islamic-umalqura',
  PS: 'islamic-umalqura',
  EG: 'islamic-umalqura',
  MA: 'islamic-umalqura',
  DZ: 'islamic-umalqura',
  TN: 'islamic-umalqura',
  LY: 'islamic-umalqura',
  IQ: 'islamic-umalqura',
  SY: 'islamic-umalqura',
  YE: 'islamic-umalqura',
  PK: 'islamic-umalqura',
  ID: 'islamic-umalqura',
  MY: 'islamic-umalqura',
  BD: 'islamic-umalqura',
};

/**
 * Locale/country-preferred calendar system. Country override wins over lang.
 *
 * NOTE: This is the *preferred* system for a locale, not the page default.
 * It is intentionally NOT used to auto-switch the interactive calendar, because
 * a non-Gregorian month grid windows to a Gregorian range that only partially
 * overlaps the month being viewed — which silently dropped events from the grid
 * (Refs: almanak-gaji). Kept for explicit opt-in / labeling use.
 */
export function getPreferredCalendarSystem(lang: string, countryCode?: string): CalendarSystem {
  if (countryCode && COUNTRY_OVERRIDES[countryCode]) {
    return COUNTRY_OVERRIDES[countryCode];
  }
  const base = lang.split('-')[0];
  return LANG_DEFAULTS[base] ?? 'gregorian';
}

/**
 * The page-default calendar system. Always Gregorian — every page renders the
 * always-correct Gregorian grid by default, and users can opt into any of the
 * other 9 systems via the CalendarSystemSelector (persisted to localStorage).
 *
 * Defaulting to Gregorian (rather than a locale-preferred lunar/lunisolar
 * system) guarantees the interactive grid shows the loaded events for the month
 * being viewed (Refs: almanak-gaji, almanak-q5al).
 */
export function getDefaultCalendarSystem(_lang: string, _countryCode?: string): CalendarSystem {
  return 'gregorian';
}
